import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { promises } from '@/data/promises';
import { formatDate } from '@/utils/date';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Link as LinkIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const PromiseDetail = () => {
  const { id } = useParams<{ id: string }>();
  
  const promise = useMemo(() => {
    return promises.find(p => p.id === id);
  }, [id]);
  
  if (!promise) {
    return (
      <div className="container px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">Promise not found</h2>
        <p className="mt-4">The promise you're looking for doesn't exist or has been removed.</p>
        <Link to="/">
          <Button className="mt-6">
            <ArrowLeft size={16} className="mr-2" />
            Back to Promise Tracker
          </Button>
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-accent pb-12">
      {/* Hero banner - Replaced with Gradient */}
      <div 
        className="w-full h-64 md:h-80 relative bg-gradient-to-br from-nepal-blue/80 via-nepal-red/60 to-nepal-blue/80"
      >
        <div className="absolute inset-0 bg-black/30 flex flex-col justify-end">
          <div className="container px-4 pb-8">
            {/* Flex container for Button */}
            <div className="flex justify-between items-center mb-4">
              <Link to="/">
                {/* Restored Button Style */}
                <Button variant="outline" size="sm" className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
                  <ArrowLeft size={16} className="mr-2" /> 
                  Back to Promise Tracker
                </Button>
              </Link>
              {/* Status Badge removed from here */}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-white max-w-3xl">{promise.title}</h1>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="container px-4 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold mb-4">Promise Details</h2>
              <p className="text-gray-700 leading-relaxed mb-6">{promise.description}</p>
              
              <h3 className="font-semibold text-lg mt-8 mb-3">Progress Timeline</h3>
              <div className="space-y-4 mb-6">
                {promise.progress.map((progress, index) => (
                  <div key={index} className="flex">
                    <div className="mr-4 relative">
                      <div className="w-3 h-3 rounded-full bg-nepal-blue mt-1.5"></div>
                      {index < promise.progress.length - 1 && (
                        <div className="absolute top-3 left-1.5 w-0.5 h-full -ml-px bg-gray-200"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-700">{progress}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <h3 className="font-semibold text-lg mt-8 mb-3">Source</h3>
              <a 
                href={promise.sourceUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-nepal-blue hover:underline"
              >
                <LinkIcon size={16} className="mr-2" />
                View original source
              </a>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <h2 className="text-lg font-semibold mb-4">Promise Information</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Politician</h3>
                  <p className="mt-1">{promise.politician}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Political Party</h3>
                  <p className="mt-1">{promise.politicalParty}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Sector</h3>
                  <p className="mt-1">{promise.sector}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Date Made</h3>
                  <p className="mt-1">{formatDate(promise.dateMade)}</p>
                </div>
                
                {promise.deadline && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Deadline</h3>
                    <p className="mt-1">{formatDate(promise.deadline)}</p>
                  </div>
                )}
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Current Status</h3>
                  <div className="mt-2">
                    <StatusBadge status={promise.status} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromiseDetail;
